/**
 * Report sections for the newer modules (#686): pain point collection and
 * systemic map v2. Data comes from useChainReportData; each section renders
 * only when the chain includes it and it has content.
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { Section, SectionTitle, SectionSubtitle, Paragraph, Divider, SITRA } from './reportStyles';
import SectionGate from './SectionGate';
import { CHAIN_NAV_KEYS } from './useChainReportData';
import ReportSolutionsV4 from './ReportSolutionsV4';
import { ReportFutureInterplayV2, ReportFutureTimelineV2 } from './ReportFutureV2';

const List = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.li`
  padding: 12px 14px;
  border-radius: 4px;
  border: 1px solid ${SITRA.border};
  background: ${SITRA.white};
  color: ${SITRA.textBody};
  .title { font-weight: 600; }
  .meta { margin-top: 4px; font-size: 14px; color: ${SITRA.textMuted}; display: flex; flex-wrap: wrap; gap: 6px 14px; }
`;

const LensDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${(p) => p.$color || '#999'};
`;

const Group = styled.div`
  margin-top: 18px;
`;

const Verdicts = ({ v, t }) => (
  <span>{t('report.chainSections.approve')} {v?.approve || 0} · {t('report.chainSections.reject')} {v?.reject || 0} · {t('report.chainSections.refine')} {v?.refine || 0}</span>
);

function titleOf(list, id) {
  return list?.find((x) => x.id === id)?.title || '';
}

export function ReportPainPoints({ painPoints }) {
  const { t } = useTranslation();
  return (
    <Section id="pain-points">
      <SectionTitle>{t('report.nav.painPoints')}</SectionTitle>
      <Paragraph>{t('report.chainSections.painPointsIntro')}</Paragraph>
      <List>
        {painPoints.clusters.map((c) => (
          <Item key={c.id}>
            <div className="title">{c.title}</div>
            <div className="meta">
              <span>{t('report.chainSections.inputsCount', { count: c.count })}</span>
              {c.status === 'promoted' && <span>{t('report.chainSections.promoted')}</span>}
            </div>
          </Item>
        ))}
      </List>
    </Section>
  );
}

export function ReportKipupisteet({ map }) {
  const { t } = useTranslation();
  const anchorCount = (id) => map.anchors?.filter((a) => a.kipupisteId === id).length || 0;
  return (
    <Section id="kipupisteet">
      <SectionTitle>{t('report.nav.kipupisteet')}</SectionTitle>
      <Paragraph>{t('report.chainSections.kipupisteetIntro')}</Paragraph>
      <List>
        {map.kipupisteet.map((k) => (
          <Item key={k.id}>
            <div className="title">{k.title}</div>
            <div className="meta"><span>{t('report.chainSections.anchorsCount', { count: anchorCount(k.id) })}</span></div>
          </Item>
        ))}
      </List>
    </Section>
  );
}

export function ReportAnkkurit({ map }) {
  const { t } = useTranslation();
  const byKipupiste = map.kipupisteet.map((k) => ({ ...k, anchors: map.anchors.filter((a) => a.kipupisteId === k.id) }))
    .filter((k) => k.anchors.length);
  return (
    <Section id="ankkurit">
      <SectionTitle>{t('report.nav.ankkurit')}</SectionTitle>
      <Paragraph>{t('report.chainSections.ankkuritIntro')}</Paragraph>
      {byKipupiste.map((k) => (
        <Group key={k.id}>
          <SectionSubtitle>{k.title}</SectionSubtitle>
          <List>
            {k.anchors.map((a) => (
              <Item key={a.id}>
                <div className="title">{a.title}</div>
                <div className="meta">
                  <span><LensDot $color={a.lens?.color} />{a.lens?.name || t('report.chainSections.noLens')}</span>
                </div>
              </Item>
            ))}
          </List>
        </Group>
      ))}
    </Section>
  );
}

export function ReportJuurisyyt({ map }) {
  const { t } = useTranslation();
  const sorted = [...map.juurisyyt].sort((a, b) => (b.verdicts?.approve || 0) - (a.verdicts?.approve || 0));
  return (
    <Section id="juurisyyt">
      <SectionTitle>{t('report.nav.juurisyyt')}</SectionTitle>
      <Paragraph>{t('report.chainSections.juurisyytIntro')}</Paragraph>
      <List>
        {sorted.map((j) => (
          <Item key={j.id}>
            <div className="title">{j.title}</div>
            <div className="meta">
              {j.kipupisteId && <span>{titleOf(map.kipupisteet, j.kipupisteId)}</span>}
              {j.lens?.name && <span><LensDot $color={j.lens.color} />{j.lens.name}</span>}
              <Verdicts v={j.verdicts} t={t} />
              {j.source === 'participant' && <span>{t('report.chainSections.byParticipant')}</span>}
            </div>
          </Item>
        ))}
      </List>
    </Section>
  );
}

export function ReportLeveragePoints({ map }) {
  const { t } = useTranslation();
  const sorted = [...map.vipupisteet].sort((a, b) => (b.kipupisteSpan || 0) - (a.kipupisteSpan || 0) || (b.degree || 0) - (a.degree || 0));
  const direction = (d) => (d === 'up_is_better' ? t('report.chainSections.upIsBetter') : d === 'up_is_worse' ? t('report.chainSections.upIsWorse') : null);
  return (
    <Section id="leverage-points">
      <SectionTitle>{t('report.nav.leveragePoints')}</SectionTitle>
      <Paragraph>{t('report.chainSections.leverageIntro')}</Paragraph>
      <List>
        {sorted.map((v) => (
          <Item key={v.id}>
            <div className="title">{v.title}{v.unit ? ` (${v.unit})` : ''}</div>
            <div className="meta">
              {direction(v.direction) && <span>{direction(v.direction)}</span>}
              {v.kipupisteSpan ? <span>{t('report.chainSections.kipupisteSpan', { count: v.kipupisteSpan })}</span> : null}
              {v.juurisyyt?.length ? <span>{t('report.chainSections.juurisyytCount', { count: v.juurisyyt.length })}</span> : null}
            </div>
          </Item>
        ))}
      </List>
    </Section>
  );
}

const PIVOT_TYPES = ['konsensus', 'kiistanalainen', 'villi_kortti', 'sokea_piste'];

/** Ongelmakartta v3's vipupisteet (Vipupisteet v2) from its latest run, by type as the room saw them */
export function ReportVipupisteetV2({ pivots }) {
  const { t } = useTranslation();
  const owner = (p) => (p.omistaja && pivots.toimijat?.[p.omistaja]) || p.omistaja_nimi || '';
  return (
    <Section id="vipupisteet-v2">
      <SectionTitle>{t('report.nav.vipupisteetV2')}</SectionTitle>
      <Paragraph>{t('report.chainSections.vipupisteetV2Intro')}</Paragraph>
      {PIVOT_TYPES.map((type) => {
        const list = pivots.vipupisteet.filter((p) => p.tyyppi === type);
        if (!list.length) return null;
        return (
          <Group key={type}>
            <SectionSubtitle>{t(`report.chainSections.pivotTypes.${type}`)}</SectionSubtitle>
            <List>
              {list.map((p) => (
                <Item key={p.id}>
                  <div className="title">{p.otsikko}{(pivots.valitut || []).includes(p.id) ? ` · ${t('report.chainSections.pivotChosen')}` : ''}</div>
                  {p.mekanismi && <div>{p.mekanismi}</div>}
                  <div className="meta">
                    {p.lahde && p.lahde !== 'huone'
                      ? <span>{t('report.chainSections.fromKnowledgeBase')}</span>
                      : <span>{t('report.chainSections.pivotReach', { juurisyyt: (p.juurisyyt || []).length, ongelmat: (p.ongelmat || []).length })}</span>}
                    {owner(p) && <span>{t('report.chainSections.pivotOwner', { name: owner(p) })}</span>}
                  </div>
                </Item>
              ))}
            </List>
          </Group>
        );
      })}
    </Section>
  );
}

export function ReportSystemicAnalysis({ analysis, map }) {
  const { t } = useTranslation();
  const c = analysis.content;
  return (
    <Section id="systemic-analysis">
      <SectionTitle>{t('report.nav.systemicAnalysis')}</SectionTitle>
      <SectionSubtitle>{t('report.chainSections.analysisOverview')}</SectionSubtitle>
      <Paragraph>{c.overview}</Paragraph>
      {c.pattern && (<><SectionSubtitle>{t('report.chainSections.analysisPattern')}</SectionSubtitle><Paragraph>{c.pattern}</Paragraph></>)}
      {c.contradictions?.length > 0 && (
        <><SectionSubtitle>{t('report.chainSections.analysisContradictions')}</SectionSubtitle>
          <ul>{c.contradictions.map((x) => <li key={x}>{x}</li>)}</ul></>
      )}
      {c.gaps?.length > 0 && (
        <><SectionSubtitle>{t('report.chainSections.analysisGaps')}</SectionSubtitle>
          <ul>{c.gaps.map((x) => <li key={x}>{x}</li>)}</ul></>
      )}
      {c.recommendation?.why && (
        <><SectionSubtitle>{t('report.chainSections.analysisRecommendation')}</SectionSubtitle>
          <Paragraph>{c.recommendation.why}</Paragraph>
          {c.recommendation.vipupisteIds?.length > 0 && (
            <ul>{c.recommendation.vipupisteIds.map((id) => <li key={id}>{titleOf(map?.vipupisteet, id)}</li>)}</ul>
          )}</>
      )}
    </Section>
  );
}

export function ReportSolutionProjections({ portfolio }) {
  const { t } = useTranslation();
  const metrics = portfolio.metrics.filter((m) => m.solutions?.length);
  return (
    <Section id="solution-projections">
      <SectionTitle>{t('report.nav.solutionProjections')}</SectionTitle>
      <Paragraph>{t('report.chainSections.projectionsIntro')}</Paragraph>
      {metrics.map((m) => (
        <Group key={m.metricId || m.vipupisteId}>
          <SectionSubtitle>{m.title}{m.unit ? ` (${m.unit})` : ''}{m.contested ? ` · ${t('report.chainSections.contested')}` : ''}</SectionSubtitle>
          <List>
            {m.solutions.map((s) => (
              <Item key={s.solutionId}>
                <div className="title">{s.title}</div>
                <div className="meta">
                  <span>{s.worsens ? t('report.chainSections.worsens') : t('report.chainSections.improves')}</span>
                  {s.at5 != null && <span>{t('report.chainSections.atFiveYears', { value: s.at5 })}</span>}
                </div>
              </Item>
            ))}
          </List>
        </Group>
      ))}
      {portfolio.untouched?.length > 0 && (
        <Paragraph>{t('report.chainSections.untouched', { list: portfolio.untouched.join(', ') })}</Paragraph>
      )}
    </Section>
  );
}

/**
 * The chain sections in report order. `ids` picks which ones this spot renders
 * (the systemic block after Esteet ja mahdollisuudet, projections after Ratkaisut).
 */
export default function ReportChainSections({ ids, data, available, show, accessOf }) {
  const { t } = useTranslation();
  const blocks = [
    ['pain-points', () => <ReportPainPoints painPoints={data.painPoints} />],
    ['kipupisteet', () => <ReportKipupisteet map={data.map} />],
    ['ankkurit', () => <ReportAnkkurit map={data.map} />],
    ['juurisyyt', () => <ReportJuurisyyt map={data.map} />],
    ['leverage-points', () => <ReportLeveragePoints map={data.map} />],
    ['vipupisteet-v2', () => <ReportVipupisteetV2 pivots={data.pivotsV3} />],
    ['systemic-analysis', () => <ReportSystemicAnalysis analysis={data.analysis} map={data.map} />],
    ['solution-projections', () => <ReportSolutionProjections portfolio={data.portfolio} />],
    ['solutions-v4', () => <ReportSolutionsV4 data={data.solutionsV4} />],
    ['future-interplay-v2', () => <ReportFutureInterplayV2 state={data.futureV2} />],
    ['future-timeline-v2', () => <ReportFutureTimelineV2 state={data.futureV2} />],
  ];
  return blocks.filter(([id]) => ids.includes(id) && available.has(id) && show(id)).map(([id, render]) => (
    <SectionGate key={id} access={accessOf(id)} gateTitle={t(`report.nav.${CHAIN_NAV_KEYS[id]}`)}>
      {render()}
      <Divider />
    </SectionGate>
  ));
}
